/* Sentio email popup — trigger + suppression wiring for the glass signup
   dialog (sections/sentio-email-popup.liquid).

   State model:
   - Server decides success/error via the customer form's contact[id] round
     trip; those states open immediately on load.
   - Otherwise a timed open, gated by localStorage caps: subscribed = never
     again, dismissed = wait `data-suppress-days`. Storage blocked (private
     mode) fails CLOSED — a popup that can't remember being dismissed would
     nag on every page.
   - ?sentio_popup=test bypasses delay + caps (preview/verification hook).
   - In the theme editor the popup only opens on section select. */
(function () {
  if (window.__sentioEmailPopupWired) return;
  window.__sentioEmailPopupWired = true;

  const SUBSCRIBED_KEY = 'sentio_popup_subscribed';
  const DISMISSED_KEY = 'sentio_popup_dismissed_at';
  // Exit-intent state is per-visit (sessionStorage): a passive close (X, Esc,
  // backdrop) arms one desktop re-show when the cursor heads for the top of
  // the window; "No thanks" is an explicit refusal and never re-arms.
  const EXIT_ARMED_KEY = 'sentio_popup_exit_armed';
  const EXIT_SHOWN_KEY = 'sentio_popup_exit_shown';
  const RETRY_MS = 15000;
  const MAX_RETRIES = 4;

  let explicitDecline = false;

  function getPopup() {
    return document.querySelector('[data-sentio-email-popup]');
  }

  function getState(popup) {
    const stateEl = popup.querySelector('[data-popup-state]');
    return stateEl ? stateEl.dataset.popupState : null;
  }

  function storageGet(key) {
    try {
      return localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (_) {
      return false;
    }
  }

  function storageAvailable() {
    try {
      localStorage.setItem('sentio_popup_probe', '1');
      localStorage.removeItem('sentio_popup_probe');
      return true;
    } catch (_) {
      return false;
    }
  }

  function sessionGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (_) {
      return false;
    }
  }

  function desktopPointer() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function openPopup(popup) {
    if (popup.open) return;
    if (typeof popup.showModal === 'function') {
      popup.showModal();
    } else {
      popup.setAttribute('open', '');
    }
    // Land focus on the heading, not the input — focusing the input would pop
    // the keyboard on mobile before the visitor has read the offer. The error
    // state's input carries an autofocus attribute that showModal honors.
    if (getState(popup) !== 'error') {
      const heading = popup.querySelector('#sentio-popup-heading');
      if (heading) heading.focus();
    }
    popup.addEventListener(
      'close',
      () => {
        if (getState(popup) !== 'success') {
          storageSet(DISMISSED_KEY, String(Date.now()));
          if (!explicitDecline && desktopPointer()) {
            sessionSet(EXIT_ARMED_KEY, '1');
          }
        }
        explicitDecline = false;
      },
      { once: true }
    );
  }

  function closePopup(popup) {
    if (typeof popup.close === 'function') {
      popup.close();
    } else {
      popup.removeAttribute('open');
    }
  }

  // Don't stack on another open modal (quick-view). The cookie banner is a
  // bottom bar, not a modal — deferring to it meant visitors who ignore the
  // banner never saw the popup at all, so it doesn't count as an overlay.
  function overlayBusy() {
    return Boolean(document.querySelector('dialog[open]'));
  }

  function suppressed(popup) {
    if (storageGet(SUBSCRIBED_KEY)) return true;
    const dismissedAt = Number(storageGet(DISMISSED_KEY));
    if (!dismissedAt) return false;
    const suppressDays = Number(popup.dataset.suppressDays) || 30;
    return Date.now() - dismissedAt < suppressDays * 864e5;
  }

  function scheduleOpen(popup, delayMs, retriesLeft) {
    setTimeout(() => {
      const current = getPopup();
      if (!current || current.open) return;
      if (overlayBusy()) {
        if (retriesLeft > 0) scheduleOpen(current, RETRY_MS, retriesLeft - 1);
        return;
      }
      openPopup(current);
    }, delayMs);
  }

  function copyCode(button) {
    const chip = button.closest('.sentio-email-popup__code-chip');
    const codeEl = chip && chip.querySelector('.sentio-email-popup__code');
    if (!codeEl) return;
    const code = codeEl.textContent.trim();

    function markCopied() {
      button.classList.add('is-copied');
      button.textContent = 'Copied ✓';
      setTimeout(() => {
        button.classList.remove('is-copied');
        button.textContent = 'Copy';
      }, 2000);
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(markCopied, () => fallbackCopy(code, markCopied));
    } else {
      fallbackCopy(code, markCopied);
    }
  }

  function fallbackCopy(text, done) {
    const scratch = document.createElement('textarea');
    scratch.value = text;
    scratch.setAttribute('readonly', '');
    scratch.style.position = 'fixed';
    scratch.style.opacity = '0';
    document.body.appendChild(scratch);
    scratch.select();
    try {
      document.execCommand('copy');
      done();
    } catch (_) {
      /* leave the code visible for manual copying */
    }
    document.body.removeChild(scratch);
  }

  // Delegated so the handlers survive editor section re-renders.
  document.addEventListener('click', (event) => {
    const popup = getPopup();
    if (!popup) return;
    const closer = event.target.closest('[data-sentio-popup-close]');
    if (closer) {
      explicitDecline = closer.classList.contains('sentio-email-popup__dismiss');
      closePopup(popup);
      return;
    }
    const copyButton = event.target.closest('[data-sentio-popup-copy]');
    if (copyButton) {
      copyCode(copyButton);
      return;
    }
    // Click on the dialog element itself = the backdrop (the glass card fills
    // the dialog, so inner clicks never target the dialog node directly).
    if (event.target === popup && popup.open) closePopup(popup);
  });

  // Exit intent: cursor leaving through the top of the viewport (toward the
  // close button / URL bar). relatedTarget is also null when entering an
  // iframe, so the clientY check is what filters those out. Installed
  // unconditionally; all gating lives in maybeExitShow.
  document.addEventListener('mouseout', (event) => {
    if (event.relatedTarget || event.clientY > 0) return;
    maybeExitShow();
  });

  function maybeExitShow() {
    const popup = getPopup();
    if (!popup || popup.open) return;
    if (popup.dataset.exitIntent === 'false') return;
    if (!desktopPointer()) return;
    if (window.Shopify && window.Shopify.designMode) return;
    if (sessionGet(EXIT_ARMED_KEY) !== '1' || sessionGet(EXIT_SHOWN_KEY)) return;
    if (storageGet(SUBSCRIBED_KEY)) return;
    if (overlayBusy()) return;
    // Storage broken → can't remember "shown once" → fail closed, no nagging.
    if (!sessionSet(EXIT_SHOWN_KEY, '1')) return;
    openPopup(popup);
  }

  function init() {
    const popup = getPopup();
    if (!popup) return;

    const params = new URLSearchParams(window.location.search);

    // Any successful customer post (footer form included) means we have the
    // email — never show the popup again on this browser.
    if (params.get('customer_posted') === 'true') {
      storageSet(SUBSCRIBED_KEY, '1');
    }

    const state = getState(popup);
    if (state === 'success') {
      openPopup(popup);
      // Strip the posted params so a refresh doesn't re-render the success
      // panel forever (the subscribed flag now owns suppression).
      params.delete('customer_posted');
      params.delete('contact_posted');
      const query = params.toString();
      history.replaceState(
        null,
        '',
        window.location.pathname + (query ? '?' + query : '')
      );
      return;
    }
    if (state === 'error') {
      openPopup(popup);
      return;
    }

    // Theme editor: open only when the merchant selects the section.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        const current = getPopup();
        if (current && event.target.contains(current)) openPopup(current);
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        const current = getPopup();
        if (current && event.target.contains(current)) closePopup(current);
      });
      return;
    }

    if (params.get('sentio_popup') === 'test') {
      // Unconditional: skips caps AND the overlay guard, so verification is
      // deterministic even with the cookie banner up on a fresh profile.
      setTimeout(() => {
        const current = getPopup();
        if (current && !current.open) openPopup(current);
      }, 500);
      return;
    }

    // The captcha interstitial renders through the theme layout; popping the
    // popup mid-challenge would interrupt this very form's submission.
    if (window.location.pathname === '/challenge') return;

    if (!storageAvailable() || suppressed(popup)) return;

    const delayMs = Number(popup.dataset.delayMs) || 3333;
    scheduleOpen(popup, delayMs, MAX_RETRIES);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
